import { useEffect, useRef } from 'react';
import {
  X, MagnifyingGlass, SortAscending, SortDescending,
  Rows, SquaresFour, Trash,
  Image as ImageIcon, Bone, ArrowsDownUp,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useAssetStore } from '../../stores/assetStore';
import { useSceneStore } from '../../stores/sceneStore';
import FolderTree from './FolderTree';
import { sortAssets, groupAssetsByType } from './assetSortUtils';
import type { AssetPanelPrefs } from './useAssetPanelPrefs';
import type { SortField } from './assetSortUtils';
import type { Asset } from '@spine-workspace/shared';

interface AssetLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  prefs: AssetPanelPrefs;
  onPrefsChange: (patch: Partial<AssetPanelPrefs>) => void;
  onDoubleClickAsset?: (asset: Asset) => void;
}

const SORT_OPTIONS: { field: SortField; label: string }[] = [
  { field: 'name', label: 'Name' },
  { field: 'type', label: 'Type' },
  { field: 'date', label: 'Date' },
  { field: 'size', label: 'Size' },
];

export default function AssetLibraryDialog({
  open,
  onClose,
  prefs,
  onPrefsChange,
  onDoubleClickAsset,
}: AssetLibraryDialogProps) {
  const {
    loading, searchQuery, setSearchQuery, tags, selectedTags, toggleTag,
    filteredAssets, deleteAsset, selectedAssetId, selectAsset,
  } = useAssetStore();

  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const raw = filteredAssets();
  const sorted = sortAssets(raw, prefs.sortField, prefs.sortDir);
  const groups = prefs.groupByType ? groupAssetsByType(sorted) : [{ label: '', items: sorted }];

  function toggleSort(field: SortField) {
    if (prefs.sortField === field) {
      onPrefsChange({ sortDir: prefs.sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      onPrefsChange({ sortField: field, sortDir: 'asc' });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm" />
        <Dialog.Content
          data-testid="asset-library-dialog"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     w-[min(900px,90vw)] h-[min(640px,85vh)]
                     bg-bg-elevated border border-border rounded-xl shadow-2xl
                     focus:outline-none flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border-subtle shrink-0">
            <Dialog.Title className="font-serif text-base font-medium text-accent tracking-[0.01em]">
              Assets
            </Dialog.Title>

            {/* Search */}
            <div className="flex-1 relative max-w-64">
              <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
              <input
                ref={searchRef}
                className="w-full bg-bg-input border border-border text-text-primary rounded-md
                           pl-7 pr-3 py-1.5 text-xs font-sans placeholder:text-text-tertiary
                           hover:bg-bg-hover focus:outline-none focus:border-accent
                           transition-all duration-normal"
                placeholder="Search assets…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Sort controls */}
            <div className="flex items-center gap-1 ml-auto">
              <ArrowsDownUp size={12} className="text-text-tertiary" />
              {SORT_OPTIONS.map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => toggleSort(field)}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    prefs.sortField === field
                      ? 'bg-accent-muted text-accent'
                      : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  {label}
                  {prefs.sortField === field && (
                    prefs.sortDir === 'asc'
                      ? <SortAscending size={9} className="inline ml-0.5" />
                      : <SortDescending size={9} className="inline ml-0.5" />
                  )}
                </button>
              ))}

              {/* Group by type toggle */}
              <button
                onClick={() => onPrefsChange({ groupByType: !prefs.groupByType })}
                title={prefs.groupByType ? 'Ungroup' : 'Group by type'}
                className={`ml-1 px-2 py-0.5 rounded text-[10px] font-mono transition-colors ${
                  prefs.groupByType
                    ? 'bg-accent-muted text-accent'
                    : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                Group
              </button>

              {/* View mode toggle */}
              <div className="flex items-center gap-0.5 ml-1 p-0.5 bg-bg-input rounded border border-border">
                <button
                  onClick={() => onPrefsChange({ dialogViewMode: 'list' })}
                  title="List view"
                  className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
                    prefs.dialogViewMode === 'list'
                      ? 'bg-accent-muted text-accent'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`}
                >
                  <Rows size={11} />
                </button>
                <button
                  onClick={() => onPrefsChange({ dialogViewMode: 'grid' })}
                  title="Grid view"
                  className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
                    prefs.dialogViewMode === 'grid'
                      ? 'bg-accent-muted text-accent'
                      : 'text-text-tertiary hover:text-text-primary'
                  }`}
                >
                  <SquaresFour size={11} />
                </button>
              </div>
            </div>

            <Dialog.Close className="w-7 h-7 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast ml-2">
              <X size={14} />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-1 min-h-0">
            {/* Left sidebar */}
            <div className="w-44 shrink-0 border-r border-border-subtle flex flex-col overflow-y-auto">
              <div className="p-2">
                <FolderTree />
              </div>

              {/* Tag filters */}
              {tags.length > 0 && (
                <div className="px-2 pb-2">
                  <p className="px-1 mb-1 text-[9px] font-mono font-semibold text-text-tertiary uppercase tracking-wide">
                    Tags
                  </p>
                  <div className="flex flex-col gap-0.5">
                    {tags.map((tag) => {
                      const active = selectedTags.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.id)}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-mono transition-colors text-left ${
                            active
                              ? 'text-white'
                              : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                          }`}
                          style={active ? { backgroundColor: tag.color } : undefined}
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Asset area */}
            <div className="flex-1 min-w-0 overflow-y-auto p-3">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-text-tertiary text-sm">
                  Loading…
                </div>
              ) : sorted.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-text-tertiary text-sm">
                  {searchQuery ? 'No matching assets' : 'No assets yet'}
                </div>
              ) : prefs.dialogViewMode === 'grid' ? (
                <>
                  {groups.map((group) => (
                    <div key={group.label}>
                      {group.label && (
                        <p className="mb-2 text-[9px] font-mono font-semibold text-text-tertiary uppercase tracking-wide">
                          {group.label}
                        </p>
                      )}
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2 mb-4">
                        {group.items.map((asset) => (
                          <DialogAssetCard
                            key={asset.id}
                            asset={asset}
                            isSelected={asset.id === selectedAssetId}
                            onSelect={() => { selectAsset(asset.id); useSceneStore.getState().selectLayer(null); }}
                            onDoubleClick={() => { onDoubleClickAsset?.(asset); onClose(); }}
                            onDelete={() => deleteAsset(asset.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {groups.map((group) => (
                    <div key={group.label}>
                      {group.label && (
                        <p className="mb-1 text-[9px] font-mono font-semibold text-text-tertiary uppercase tracking-wide">
                          {group.label}
                        </p>
                      )}
                      <div className="mb-4">
                        {group.items.map((asset) => (
                          <DialogAssetRow
                            key={asset.id}
                            asset={asset}
                            isSelected={asset.id === selectedAssetId}
                            onSelect={() => { selectAsset(asset.id); useSceneStore.getState().selectLayer(null); }}
                            onDoubleClick={() => { onDoubleClickAsset?.(asset); onClose(); }}
                            onDelete={() => deleteAsset(asset.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-border-subtle shrink-0 flex items-center justify-between">
            <span className="text-[10px] font-mono text-text-tertiary">
              {sorted.length} asset{sorted.length !== 1 ? 's' : ''}
            </span>
            <span className="text-[10px] font-mono text-text-tertiary">
              Double-click to add to scene
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Dialog list row ───────────────────────────────────────────────────────────

function DialogAssetRow({
  asset,
  isSelected,
  onSelect,
  onDoubleClick,
  onDelete,
}: {
  asset: Asset;
  isSelected: boolean;
  onSelect: () => void;
  onDoubleClick: () => void;
  onDelete: () => void;
}) {
  const isImage = asset.spineVersion === 'image';
  const anims = asset.metadata?.animations?.length ?? 0;
  const skins = asset.metadata?.skins?.length ?? 0;
  const sizeKB = Math.round(asset.fileSize / 1024);

  return (
    <div
      data-testid={`dialog-asset-row-${asset.id}`}
      draggable
      className={`group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-accent-muted outline outline-[1.5px] outline-accent/30' : 'hover:bg-bg-hover'
      }`}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      onDragStart={(e) => {
        e.dataTransfer.setData('application/spine-asset', JSON.stringify([asset]));
        e.dataTransfer.effectAllowed = 'copy';
      }}
    >
      {/* Icon */}
      <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${
        isImage ? 'bg-emerald-500/10' : 'bg-accent/10'
      }`}>
        {isImage
          ? <ImageIcon size={16} className="text-emerald-400" />
          : <Bone size={16} className="text-accent/70" />
        }
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm font-medium text-text-primary truncate">{asset.name}</p>
        <p className="font-mono text-[10px] text-text-tertiary">
          {isImage ? `Image · ${sizeKB} KB` : `${anims} anims · ${skins} skins · ${sizeKB} KB`}
        </p>
        {(asset.tags ?? []).length > 0 && (
          <div className="flex gap-1 mt-0.5 flex-wrap">
            {(asset.tags ?? []).map((t) => (
              <span
                key={t.id}
                className="px-1 py-px rounded text-[8px] font-mono text-white"
                style={{ backgroundColor: t.color }}
              >{t.name}</span>
            ))}
          </div>
        )}
      </div>

      {/* Type badge */}
      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${
        isImage ? 'bg-emerald-500/15 text-emerald-400' : 'bg-accent/15 text-accent'
      }`}>
        {isImage ? 'IMG' : 'SP'}
      </span>

      {/* Delete */}
      <button
        className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all"
        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete "${asset.name}"?`)) onDelete(); }}
        title="Delete"
      >
        <Trash size={12} />
      </button>
    </div>
  );
}

// ── Dialog grid card ──────────────────────────────────────────────────────────

function DialogAssetCard({
  asset,
  isSelected,
  onSelect,
  onDoubleClick,
  onDelete,
}: {
  asset: Asset;
  isSelected: boolean;
  onSelect: () => void;
  onDoubleClick: () => void;
  onDelete: () => void;
}) {
  const isImage = asset.spineVersion === 'image';

  return (
    <div
      data-testid={`dialog-asset-card-${asset.id}`}
      draggable
      className={`group relative flex flex-col rounded-lg cursor-pointer transition-colors overflow-hidden ${
        isSelected ? 'bg-accent-muted outline outline-[1.5px] outline-accent/30' : 'bg-bg-surface hover:bg-bg-hover border border-border'
      }`}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      onDragStart={(e) => {
        e.dataTransfer.setData('application/spine-asset', JSON.stringify([asset]));
        e.dataTransfer.effectAllowed = 'copy';
      }}
    >
      {/* Preview area */}
      <div className={`h-20 flex items-center justify-center ${isImage ? 'bg-emerald-500/5' : 'bg-accent/5'}`}>
        {isImage
          ? <ImageIcon size={28} className="text-emerald-400/40" />
          : <Bone size={28} className="text-accent/30" />
        }
      </div>

      {/* Name */}
      <div className="p-2">
        <p className="font-sans text-xs font-medium text-text-primary truncate">{asset.name}</p>
        <p className="font-mono text-[9px] text-text-tertiary mt-0.5">
          {isImage ? 'Image' : `${asset.metadata?.animations?.length ?? 0} anims`}
        </p>
      </div>

      {/* Type badge overlay */}
      <span className={`absolute top-1.5 right-1.5 px-1 py-px rounded text-[7px] font-mono font-bold ${
        isImage ? 'bg-emerald-500/80 text-white' : 'bg-accent/70 text-white'
      }`}>
        {isImage ? 'IMG' : 'SP'}
      </span>

      {/* Delete overlay */}
      <button
        className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded bg-error-muted text-error transition-all"
        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete "${asset.name}"?`)) onDelete(); }}
        title="Delete"
      >
        <Trash size={9} />
      </button>
    </div>
  );
}
